/*
 * Single source of truth for per-route SEO metadata.
 *
 * Consumed by:
 *   - React pages via <SEO> (src/App.tsx, src/pages/ReleaseNotes.tsx,
 *     src/pages/Privacy.tsx, src/pages/Contact.tsx)
 *   - the build-time prerender script (scripts/prerender.js)
 *
 * The transformation helpers below MIRROR src/Components/SEO.tsx so that the
 * statically prerendered <head> matches what react-helmet-async renders after
 * hydration. If you change a value here, SEO.tsx must keep producing the same
 * output (it derives title/canonical/JSON-LD the same way).
 *
 * Plain CommonJS on purpose: Node (the prerender script) and the CRA/webpack
 * bundle can both consume it. Types live in seoRoutes.d.ts.
 */

const SITE_URL = 'https://www.tcgcalendar.com.au';
const SITE_NAME = 'TCG Calendar Australia';
const DEFAULT_TITLE = 'TCG Calendar Australia - Trading Card Game Events & Tournaments';
const DEFAULT_DESCRIPTION = 'Find Pokemon, One Piece, Magic: The Gathering and trading card game events across Australia. Browse upcoming tournaments, league events, prereleases and competitions near you.';
const DEFAULT_IMAGE = `${SITE_URL}/tcg-calendar-social.png`;
const BRAND_LOGO = `${SITE_URL}/tcg-calendar-brand-logo.png`;
const DEFAULT_IMAGE_ALT = 'TCG Calendar Australia logo';

const HOME_SEO_DESCRIPTION = "Browse trading card shows, conventions and collector events across Australia. Find tournaments and organised play in each trading card game's dedicated tab.";
const HOME_COMPACT_SUMMARY = 'Shows, conventions and collector events nationwide.';
const HOME_DISCOVERY_INTRO = 'Shows lists trading card shows, conventions and collector events across Australia. For tournaments and organised play, use a dedicated game calendar.';
const HOME_DISCOVERY_DETAIL = 'Each game calendar includes Australia-wide and state views. Search shows by title or location.';
const CARDREVIVE_INTRO_URL = 'https://www.cardrevive.com.au/?utm_source=tcgcalendar&utm_medium=referral&utm_campaign=intro';
const CARDHUB_INTRO_URL = 'https://linktr.ee/thecardhubaustralia';
const SUPPORTER_INTRO_TEXT = "TCG Calendar is supported by CardRevive, Australia's trading card restoration and grading service, and The Card Hub Australia.";
const HOME_GAME_LINKS = [
  { label: 'Pokemon competitions', path: '/pokemon-competitions' },
  { label: 'One Piece tournaments', path: '/onepiece-competitions' },
  { label: 'Magic: The Gathering tournaments', path: '/magic-competitions' },
  { label: 'Riftbound events', path: '/riftbound-competitions' },
  { label: 'Disney Lorcana events', path: '/lorcana-competitions' },
  { label: 'Flesh and Blood events', path: '/fleshandblood-competitions' },
];
const SUPPORTER_INTRO_PATHS = new Set(['/', ...HOME_GAME_LINKS.map(({ path }) => path)]);
const POKEMON_SEO_DESCRIPTION = 'Browse official Pokemon TCG competitions across Australian states. Find League Challenges, League Cups, prereleases and tournament events near you.';
const ONEPIECE_SEO_DESCRIPTION = 'Browse One Piece Card Game tournaments across Australia. Find regional events, championships and competitive One Piece TCG events near you.';
const MAGIC_SEO_DESCRIPTION = 'Browse Magic: The Gathering tournaments and events across Australia. Find Regional Championships, Friday Night Magic and competitive MTG events near you.';
const RIFTBOUND_SEO_DESCRIPTION = 'Browse Riftbound events and tournaments across Australia. Find locals, skirmishes and competitive Riftbound TCG events near you.';
const LORCANA_SEO_DESCRIPTION = 'Browse Disney Lorcana events and tournaments across Australia. Find locals, store championships and competitive Disney Lorcana events near you.';
const FLESHANDBLOOD_SEO_DESCRIPTION = 'Browse Flesh and Blood events and tournaments across Australia. Find Armory events, skirmishes and competitive Flesh and Blood events near you.';
const SETS_SEO_DESCRIPTION = 'Track Pokemon, One Piece, Magic: The Gathering and other TCG set release dates for Australian collectors.';

const SET_GAME_LABELS = {
  pokemon: 'Pokemon TCG',
  one_piece: 'One Piece Card Game',
  onepiece: 'One Piece Card Game',
  magic: 'Magic: The Gathering',
  mtg: 'Magic: The Gathering',
  lorcana: 'Disney Lorcana',
  digimon: 'Digimon Card Game',
  dragon_ball: 'Dragon Ball Super Card Game',
  dragonball: 'Dragon Ball Super Card Game',
  riftbound: 'Riftbound',
  flesh_and_blood: 'Flesh and Blood',
  fleshandblood: 'Flesh and Blood',
};

// Australian states that appear as /:stateCode route segments.
// Mirrors AU_STATE_OPTIONS in src/constants/index.ts (minus the "ALL" sentinel).
// URL segments are the lowercased code; SEO copy uses the full label.
const AU_STATES = [
  { code: 'ACT', label: 'Australian Capital Territory' },
  { code: 'NSW', label: 'New South Wales' },
  { code: 'NT', label: 'Northern Territory' },
  { code: 'QLD', label: 'Queensland' },
  { code: 'SA', label: 'South Australia' },
  { code: 'TAS', label: 'Tasmania' },
  { code: 'VIC', label: 'Victoria' },
  { code: 'WA', label: 'Western Australia' },
];

const stateLabel = (code) => {
  const match = AU_STATES.find((state) => state.code === code);
  return match ? match.label : code;
};

const statePath = (basePath, stateCode) => (
  stateCode === 'ALL' ? basePath : `${basePath}/${String(stateCode).toLowerCase()}`
);

const hasSupporterIntro = (routePath) => SUPPORTER_INTRO_PATHS.has(normalizePath(routePath));

// --- transformation helpers (mirror src/Components/SEO.tsx) ---

const normalizePath = (path) => {
  if (!path || path === '/') return '/';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return cleanPath.replace(/\/+$/, '');
};

const absoluteUrl = (pathOrUrl) => {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${normalizePath(pathOrUrl)}`;
};

const buildPageTitle = (title) => (title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE);

const buildBreadcrumbJsonLd = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length < 2) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
};

const buildBaseJsonLd = ({ pageTitle, canonicalUrl, description, schemaType }) => ({
  '@context': 'https://schema.org',
  '@type': schemaType || 'WebPage',
  name: pageTitle,
  url: canonicalUrl,
  description,
  inLanguage: 'en-AU',
  isPartOf: {
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: BRAND_LOGO,
  },
});

// --- per-route metadata builders ---

const COMPETITION_CONFIG = {
  pokemon: {
    basePath: '/pokemon-competitions',
    seoTitleBase: 'Pokemon Competitions',
    headingBase: 'Pokemon TCG Competitions',
    breadcrumbName: 'Pokemon Competitions',
    allDescription: POKEMON_SEO_DESCRIPTION,
    stateDescription: (label) => `Browse official Pokemon TCG competitions in ${label}. Find League Challenges, League Cups, prereleases and tournament events near you.`,
  },
  onepiece: {
    basePath: '/onepiece-competitions',
    seoTitleBase: 'One Piece Tournaments',
    headingBase: 'One Piece TCG Tournaments',
    breadcrumbName: 'One Piece Tournaments',
    allDescription: ONEPIECE_SEO_DESCRIPTION,
    stateDescription: (label) => `Browse One Piece Card Game tournaments in ${label}. Find regional events, championships and competitive One Piece TCG events near you.`,
  },
  magic: {
    basePath: '/magic-competitions',
    seoTitleBase: 'MTG Tournaments',
    headingBase: 'Magic: The Gathering Tournaments',
    breadcrumbName: 'MTG Tournaments',
    allDescription: MAGIC_SEO_DESCRIPTION,
    stateDescription: (label) => `Browse Magic: The Gathering tournaments and events in ${label}. Find Regional Championships, Friday Night Magic and competitive MTG events near you.`,
  },
  riftbound: {
    basePath: '/riftbound-competitions',
    seoTitleBase: 'Riftbound Events',
    headingBase: 'Riftbound Events',
    titleSuffix: 'Australia',
    breadcrumbName: 'Riftbound Events',
    allDescription: RIFTBOUND_SEO_DESCRIPTION,
    stateDescription: (label) => `Browse Riftbound events and tournaments in ${label}. Find locals, skirmishes and competitive Riftbound TCG events near you.`,
  },
  lorcana: {
    basePath: '/lorcana-competitions',
    seoTitleBase: 'Disney Lorcana Events',
    headingBase: 'Disney Lorcana Events',
    titleSuffix: 'Australia',
    breadcrumbName: 'Disney Lorcana Events',
    allDescription: LORCANA_SEO_DESCRIPTION,
    stateDescription: (label) => `Browse Disney Lorcana events and tournaments in ${label}. Find locals, store championships and competitive Disney Lorcana events near you.`,
  },
  fleshandblood: {
    basePath: '/fleshandblood-competitions',
    seoTitleBase: 'Flesh and Blood Events',
    headingBase: 'Flesh and Blood Events',
    titleSuffix: 'Australia',
    breadcrumbName: 'Flesh and Blood Events',
    allDescription: FLESHANDBLOOD_SEO_DESCRIPTION,
    stateDescription: (label) => `Browse Flesh and Blood events and tournaments in ${label}. Find Armory events, skirmishes and competitive Flesh and Blood events near you.`,
  },
};

const buildCompetitionMeta = (gameKey, stateCode) => {
  const config = COMPETITION_CONFIG[gameKey];
  const isAll = stateCode === 'ALL';
  const label = stateLabel(stateCode);
  const path = statePath(config.basePath, stateCode);
  // Home > hub always applies. State variants get a third crumb for the
  // specific state, pointing at the state page itself; the hub crumb always
  // points at the "all states" root rather than reusing the state path, so
  // the trail actually reflects the site hierarchy instead of collapsing the
  // hub and the state page into one node.
  const breadcrumbs = [
    { name: SITE_NAME, path: '/' },
    { name: config.breadcrumbName, path: config.basePath },
  ];
  if (!isAll) {
    breadcrumbs.push({ name: label, path });
  }
  // Optional trailing token (e.g. "Australia") for games whose title reads
  // better with it; omitted when unset so the existing games are unchanged.
  const suffix = config.titleSuffix ? ` ${config.titleSuffix}` : '';
  return {
    title: isAll ? `${config.seoTitleBase}${suffix}` : `${config.seoTitleBase} ${label}${suffix}`,
    heading: `${config.headingBase}${isAll ? '' : ` ${label}`} Australia`,
    path,
    description: isAll ? config.allDescription : config.stateDescription(label),
    schemaType: 'CollectionPage',
    breadcrumbs,
  };
};

const buildHomeMeta = () => ({
  title: 'Trading Card Shows Australia',
  heading: 'Trading Card Shows Australia',
  path: '/',
  description: HOME_SEO_DESCRIPTION,
  schemaType: 'CollectionPage',
  breadcrumbs: [
    { name: SITE_NAME, path: '/' },
  ],
});

const buildReleaseNotesMeta = () => ({
  title: 'Release Notes',
  heading: '📋 TCG Calendar - Release Notes',
  path: '/release-notes',
  description: "TCG Calendar Australia changelog and release notes. See what's new in the latest version.",
  breadcrumbs: [
    { name: SITE_NAME, path: '/' },
    { name: 'Release Notes', path: '/release-notes' },
  ],
});

const buildPrivacyMeta = () => ({
  title: 'Privacy Policy',
  heading: 'Privacy Policy',
  path: '/privacy',
  description: 'How TCG Calendar Australia collects, uses and stores personal information: accounts, saved events, transactional email, analytics and how to request data deletion.',
  breadcrumbs: [
    { name: SITE_NAME, path: '/' },
    { name: 'Privacy Policy', path: '/privacy' },
  ],
});

const buildPartnersMeta = () => ({
  title: 'Partners: CardRevive and The Card Hub',
  heading: 'Our Partners and Co-founders',
  path: '/partners',
  description: 'Meet CardRevive and The Card Hub Australia, co-founders of TCG Calendar, plus CardSurvive slab protectors, the AI Card Grader, Inspector and Client Portal.',
  schemaType: 'AboutPage',
  breadcrumbs: [
    { name: SITE_NAME, path: '/' },
    { name: 'Partners', path: '/partners' },
  ],
});

const buildContactMeta = () => ({
  title: 'Contact',
  heading: 'Contact TCG Calendar Australia',
  path: '/contact',
  description: 'Contact the TCG Calendar Australia team: send us a message, submit a trading card show, request event promotion or report a correction for an event listing.',
  breadcrumbs: [
    { name: SITE_NAME, path: '/' },
    { name: 'Contact', path: '/contact' },
  ],
});

// Sets are deliberately NOT part of getSubpageRouteMetas(). The hub and its
// database-backed detail pages are served through api/set-page.js so the
// established 58-file static prerender contract remains unchanged.
const buildSetsHubMeta = () => ({
  title: 'TCG Set Release Calendar',
  heading: 'TCG Set Release Calendar',
  path: '/sets',
  description: SETS_SEO_DESCRIPTION,
  schemaType: 'CollectionPage',
  breadcrumbs: [
    { name: SITE_NAME, path: '/' },
    { name: 'Sets', path: '/sets' },
  ],
});

const setGameLabel = (game) => SET_GAME_LABELS[String(game || '').trim().toLowerCase()] || '';

const normaliseSetNameForAliasMatch = (value) => String(value || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const nameAlreadyCarriesGame = (name, game) => {
  const normalisedName = normaliseSetNameForAliasMatch(name);
  const aliases = {
    pokemon: ['pokemon'],
    one_piece: ['one piece'],
    onepiece: ['one piece'],
    magic: ['magic', 'mtg'],
    mtg: ['magic', 'mtg'],
    lorcana: ['lorcana'],
    digimon: ['digimon'],
    dragon_ball: ['dragon ball'],
    dragonball: ['dragon ball'],
    riftbound: ['riftbound'],
    flesh_and_blood: ['flesh and blood', 'flesh blood', 'fab'],
    fleshandblood: ['flesh and blood', 'flesh blood', 'fab'],
  }[String(game || '').trim().toLowerCase()] || [];
  const paddedName = ` ${normalisedName} `;
  return aliases.some((alias) => paddedName.includes(` ${alias} `));
};

const validSetDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.getUTCFullYear() === Number(match[1])
    && date.getUTCMonth() + 1 === Number(match[2])
    && date.getUTCDate() === Number(match[3])
    ? `${match[1]}-${match[2]}-${match[3]}`
    : '';
};

const normaliseSetDateRegion = (release) => {
  if (validSetDate(release && release.australian_release_date)) return 'australia';
  if (!validSetDate(release && release.release_date)) {
    return validSetDate(release && release.global_release_date) ? 'global' : 'general';
  }
  const value = String((release && release.release_date_region) || '').trim().toLowerCase();
  if (['australia', 'au', 'australian'].includes(value)) return 'australia';
  if (['global', 'worldwide'].includes(value)) return 'global';
  if (['us', 'usa', 'united-states', 'united states'].includes(value)) return 'us';
  return 'general';
};

const isExpectedAustralianSetDate = (release) => (
  !validSetDate(release && release.australian_release_date)
  && Boolean(validSetDate(release && release.release_date))
  && String((release && release.release_date_region) || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase() === 'expected australian retail'
);

const buildSetDetailMeta = (setRelease) => {
  const release = setRelease || {};
  const name = String(release.name || '').trim() || 'TCG Set';
  const slug = String(release.slug || '').trim();
  const game = String(release.game || '').trim().toLowerCase();
  const gameLabel = setGameLabel(game);
  const titleName = gameLabel && !nameAlreadyCarriesGame(name, game)
    ? `${name} ${gameLabel}`
    : name;
  const gamePhrase = gameLabel && !nameAlreadyCarriesGame(name, game)
    ? `, a ${gameLabel} set`
    : '';
  const expectedDate = isExpectedAustralianSetDate(release);
  const status = String(release.status || '').trim().toLowerCase();
  const statusLabel = expectedDate
    ? ''
    : (status === 'rumoured' ? 'Rumoured' : (status === 'delayed' ? 'Delayed' : ''));
  const dateQualifier = statusLabel ? `${statusLabel.toLowerCase()} ` : '';
  const dateRegion = normaliseSetDateRegion(release);
  const titleSuffix = expectedDate
    ? 'Expected Australian Release Date'
    : dateRegion === 'australia'
    ? 'Release Date Australia'
    : dateRegion === 'global'
      ? 'Global Release Date for Australian Collectors'
      : dateRegion === 'us'
        ? 'US Release Date for Australian Collectors'
        : 'Release Date for Australian Collectors';
  const descriptionLead = expectedDate
    ? `View the expected Australian retail release date for ${name}${gamePhrase}`
    : dateRegion === 'australia'
    ? `View the ${dateQualifier}Australian release date for ${name}${gamePhrase}`
    : dateRegion === 'global'
      ? `View the ${dateQualifier}global release date for ${name}${gamePhrase}, with information for Australian collectors`
      : dateRegion === 'us'
        ? `View the ${dateQualifier}US release date for ${name}${gamePhrase}, with information for Australian collectors`
        : `View ${dateQualifier}release information for ${name}${gamePhrase}, prepared for Australian collectors`;

  return {
    title: `${titleName} ${titleSuffix}${statusLabel ? ` (${statusLabel})` : ''}`,
    heading: name,
    path: `/sets/${slug}`,
    description: `${descriptionLead}, plus related major TCG events.`,
    schemaType: 'WebPage',
    breadcrumbs: [
      { name: SITE_NAME, path: '/' },
      { name: 'Sets', path: '/sets' },
      { name, path: `/sets/${slug}` },
    ],
  };
};

const buildSetsItemListJsonLd = (setReleases) => {
  const releases = Array.isArray(setReleases) ? setReleases : [];
  const itemListElement = releases
    .filter((release) => release && release.is_active !== false)
    .map((release) => ({
      name: String(release.name || '').trim(),
      slug: String(release.slug || '').trim(),
    }))
    .filter(({ name, slug }) => name && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug))
    .map(({ name, slug }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name,
      url: absoluteUrl(`/sets/${slug}`),
    }));
  if (itemListElement.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'TCG set release calendar for Australian collectors',
    itemListElement,
  };
};

// Every static public subpage that the prerender script emits a head for
// (the homepage is handled separately because it owns build/index.html).
const getSubpageRouteMetas = () => {
  const metas = [buildReleaseNotesMeta(), buildPrivacyMeta(), buildContactMeta(), buildPartnersMeta()];
  ['pokemon', 'onepiece', 'magic', 'riftbound', 'lorcana', 'fleshandblood'].forEach((gameKey) => {
    metas.push(buildCompetitionMeta(gameKey, 'ALL'));
    AU_STATES.forEach((state) => metas.push(buildCompetitionMeta(gameKey, state.code)));
  });
  return metas;
};

module.exports = {
  SITE_URL,
  SITE_NAME,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  DEFAULT_IMAGE_ALT,
  HOME_SEO_DESCRIPTION,
  HOME_COMPACT_SUMMARY,
  HOME_DISCOVERY_INTRO,
  HOME_DISCOVERY_DETAIL,
  CARDREVIVE_INTRO_URL,
  CARDHUB_INTRO_URL,
  SUPPORTER_INTRO_TEXT,
  HOME_GAME_LINKS,
  POKEMON_SEO_DESCRIPTION,
  ONEPIECE_SEO_DESCRIPTION,
  MAGIC_SEO_DESCRIPTION,
  RIFTBOUND_SEO_DESCRIPTION,
  LORCANA_SEO_DESCRIPTION,
  FLESHANDBLOOD_SEO_DESCRIPTION,
  SETS_SEO_DESCRIPTION,
  AU_STATES,
  stateLabel,
  statePath,
  hasSupporterIntro,
  normalizePath,
  absoluteUrl,
  buildPageTitle,
  buildBreadcrumbJsonLd,
  buildBaseJsonLd,
  buildCompetitionMeta,
  buildHomeMeta,
  buildReleaseNotesMeta,
  buildPrivacyMeta,
  buildContactMeta,
  buildPartnersMeta,
  buildSetsHubMeta,
  buildSetDetailMeta,
  buildSetsItemListJsonLd,
  getSubpageRouteMetas,
};
